/* OC25 direct-checkout adapter. Preserves the OC23 Shopify/Skio add payload and gift validation. */
(() => {
  'use strict';
  if (window.GndBundleCart) return;
  const source = 'definitive-pdp';
  const gifts = Object.freeze({
    guarantee: 63446695837836,
    shipping: 63446695903372,
    mystery: 63446696558732,
    draw: 63446702030988
  });
  const originalGifts = {gift_1:'guarantee',gift_2:'shipping',gift_3:'mystery',gift_4:'draw'};
  const root = () => window.Shopify?.routes?.root || '/';
  const members = (cart, group) => (cart.items || []).filter(item => item.properties?._gnd_source === source && item.properties._gnd_group === group);
  const errorMessage = payload => typeof payload?.description === 'string' ? payload.description : typeof payload?.message === 'string' ? payload.message : 'The complete bundle could not be added. Please try again.';
  async function request(path, body) {
    const response = await fetch(root() + path, {
      method: body ? 'POST' : 'GET', credentials:'same-origin', cache:'no-store',
      headers:{Accept:'application/json',...(body ? {'Content-Type':'application/json','X-GND-Bundle':'1'} : {})},
      ...(body ? {body:JSON.stringify(body)} : {})
    });
    const payload = await response.json();
    if (!response.ok || payload.status || payload.errors) {
      const error = new Error(errorMessage(payload)); error.status = response.status; throw error;
    }
    return payload;
  }
  function planFor(product) {
    if (!product?.id || !product.available) throw new Error('This bundle is not available yet.');
    let plans = (product.plans || []).filter(plan => plan.recurring && Number.isSafeInteger(Number(plan.id)) && Number(plan.id) > 0);
    const skioPlans = plans.filter(plan => String(plan.appId || '').toUpperCase() === 'SKIO');
    if (skioPlans.length) plans = skioPlans;
    // Allocations belong to the selected product variant. Never reuse another bundle's plan.
    const monthly = plans.filter(plan => [plan.name, ...(plan.options || []).map(option => option.value)].some(value =>
      /\b(?:monthly|(?:every\s+)?(?:1\s+months?|30\s+days?)|every\s+month)\b/i.test(String(value || '').replace(/[-_]/g,' '))
    ));
    const selected = monthly.length === 1 ? monthly[0] : plans.length === 1 ? plans[0] : null;
    if (!selected) throw new Error('The subscription for this bundle is not available yet.');
    return selected;
  }
  const productCache = new Map();
  const productFreshness = 30000;
  async function loadProduct(product) {
    if (!product?.handle) throw new Error('This bundle is not available yet. Please refresh the page.');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    let data;
    try {
      const response = await fetch(root()+'products/'+encodeURIComponent(product.handle)+'.js', {
        credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}, signal:controller.signal
      });
      if (!response.ok) throw new Error('This bundle is temporarily unavailable. Please try another option.');
      data = await response.json();
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error('Could not check this bundle. Please check your connection and try again.');
      throw error;
    } finally { clearTimeout(timeout); }
    const variant = product.overrideId
      ? data.variants?.find(item => Number(item.id) === Number(product.overrideId))
      : data.variants?.find(item => item.available) || data.variants?.[0];
    if (!variant) throw new Error('This bundle is temporarily unavailable. Please try another option.');
    const plans = (variant.selling_plan_allocations || []).flatMap(allocation => {
      const group = (data.selling_plan_groups || []).find(item => item.id === allocation.selling_plan_group_id);
      const plan = group?.selling_plans?.find(item => Number(item.id) === Number(allocation.selling_plan_id));
      return plan ? [{id:plan.id,name:plan.name,appId:group.app_id,recurring:plan.recurring_deliveries,options:plan.options,price:allocation.price,prices:allocation.price_adjustments,adjustments:plan.price_adjustments}] : [];
    });
    return {...product,id:variant.id,available:variant.available,type:data.type,plans};
  }
  function refreshProduct(product) {
    if (!product?.handle) return Promise.reject(new Error('This bundle is not available yet. Please refresh the page.'));
    const key = JSON.stringify([root(),window.Shopify?.currency?.active,product.handle,product.overrideId || null]);
    const cached = productCache.get(key);
    if (cached && (cached.pending || Date.now()-cached.time < productFreshness)) return cached.promise;
    const entry = {pending:true,time:0};
    entry.promise = loadProduct(product).then(value => {
      entry.pending=false;entry.time=Date.now();return value;
    },error => {
      if(productCache.get(key)===entry)productCache.delete(key);
      throw error;
    });
    productCache.set(key,entry);
    return entry.promise;
  }
  function buildItems(products, bundle, group, eligibleGifts) {
    if (!['starter','duo','complete'].includes(bundle.key)) throw new Error('Please select a bundle.');
    const product = products[bundle.key];
    const plan = planFor(product);
    const productType = String(product.type || '').trim().toLowerCase();
    if (productType === 'dynamic box') throw new Error('This bundle is not available yet.');
    const props = role => ({_gnd_source:source,_gnd_group:group,_gnd_bundle:bundle.key,_gnd_title:bundle.title,_gnd_role:role});
    // Skio Preset boxes need only their parent variant; Skio adds their contents after checkout.
    // Keep our gift-group metadata separate from Skio's internal box selection properties.
    const properties = props('bundle');
    if (productType === 'classic box') properties._isClassicBoxV2 = true;
    if (bundle.contents) properties._gnd_contents = String(bundle.contents).slice(0,240);
    if (bundle.images?.length) properties._gnd_images = JSON.stringify(bundle.images.slice(0,3));
    const items = [{id:Number(product.id),quantity:1,selling_plan:Number(plan.id),properties}];
    const seen = new Set();
    for (const gift of eligibleGifts) {
      const kind = gift.kind === 'auto' ? originalGifts[gift.key] : gift.kind;
      if (!gifts[kind]) throw new Error('A gift is not configured for this bundle.');
      if (seen.has(kind)) continue;
      seen.add(kind);
      items.push({id:gifts[kind],quantity:1,properties:props('gift')});
    }
    return items;
  }
  function complete(items, expected) {
    return items.length === expected.length && expected.every(wanted => items.some(item =>
      Number(item.variant_id || item.id) === wanted.id && item.quantity === wanted.quantity &&
      item.properties?._gnd_role === wanted.properties._gnd_role &&
      (!wanted.properties._isClassicBoxV2 || String(item.properties._isClassicBoxV2) === 'true') &&
      Number(item.selling_plan_allocation?.selling_plan?.id || 0) === Number(wanted.selling_plan || 0) &&
      (wanted.properties._gnd_role !== 'gift' || item.final_line_price === 0)
    ));
  }
  async function remove(group, cart, retry=true) {
    const supplied = Boolean(cart);
    cart = cart || await request('cart.js');
    const lines = members(cart, group);
    if (!lines.length) return cart;
    const updates = Object.fromEntries(lines.map(item => [item.key,0]));
    let updated;
    try { updated = await request('cart/update.js', {updates}); }
    catch(error) {
      // Another tab can change a line key. Re-read only on that exceptional path.
      if(supplied && retry && [400,404,422].includes(error.status))return remove(group,undefined,false);
      throw error;
    }
    if (members(updated,group).length) {
      if(supplied && retry)return remove(group,updated,false);
      throw new Error('The bundle could not be removed. Please try again.');
    }
    return updated;
  }
  let navigating = false;
  async function render(cart, open) {
    document.dispatchEvent(new CustomEvent('gnd:cart-updated'));
    if (!open) return;
    navigating = true;
    // Shopify's native cart mutation remains observable to its customer-event pixels.
    // No manual Meta event is emitted: that would risk double counting the native event.
    document.querySelector('gnd-offer')?.setAttribute('data-checkout-state', 'confirmed');
    location.assign(root() + 'checkout');
  }
  async function add(items, group, trigger) {
    let verifiedCart;
    try {
      const payload=await request('cart/add.js',{items,sections:['oc25-cart-snapshot'],sections_url:location.pathname});
      // Bundled sections render after the mutation. A tiny JSON section returns the
      // real full cart in the same response; no second network round trip is needed.
      // Section rendering can fail independently of an add: fall back to GET, never POST again.
      let snapshot;
      try {
        const html=payload.sections?.['oc25-cart-snapshot'];
        if(html){
          snapshot=JSON.parse(new DOMParser().parseFromString(html,'text/html').querySelector('[data-oc25-cart-snapshot]').textContent);
          if(!Array.isArray(snapshot?.items) || !snapshot.currency || !Number.isInteger(snapshot.item_count))snapshot=null;
        }
      } catch(_){snapshot=null;}
      const cart = snapshot || await request('cart.js');
      if (!complete(members(cart,group),items)) throw new Error('The complete bundle is not available.');
      verifiedCart = cart;
    } catch (error) {
      productCache.clear();
      let confirmed;
      try {
        const cart = await request('cart.js');
        if (complete(members(cart,group),items)) {
          confirmed = cart;
        } else {
          const cleaned = await remove(group,cart);
          await render(cleaned,false);
        }
      } catch (_) {
        const uncertain = new Error('Please check your cart before trying again. The bundle could not be confirmed.');
        uncertain.cartUnconfirmed = true;
        throw uncertain;
      }
      if (confirmed) {
        // A lost add response is not a failed purchase if the entire group is in the cart.
        try { await render(confirmed,true,trigger); }
        catch (_) { location.assign(root()+'checkout'); }
        return;
      }
      throw error;
    }
    // A drawer rendering failure must not turn a successful add into another add attempt.
    try { await render(verifiedCart,true,trigger); }
    catch (_) { location.assign(root()+'checkout'); }
  }
  let adding = false;
  async function addWithCartLock(...args) {
    if (adding || navigating) throw new Error('Your bundle is already being added. Please wait.');
    adding = true;
    try { return await add(...args); }
    finally { adding = false; }
  }
  window.addEventListener('pageshow', () => { navigating = false; adding = false; });
  window.GndBundleCart = {buildItems,planFor,refreshProduct,add:addWithCartLock,remove,members,complete,gifts};
  // Warm only the selected offer near the viewport or on purchase intent. Never poll,
  // never write a cart, and share the same short-lived promise with the real ATC.
  const warm = (offer, radio) => {
    if(!offer || document.visibilityState==='hidden' || navigator.connection?.saveData)return;
    try {
      radio=radio || offer.querySelector('[data-bundle]:checked') || offer.querySelector('[data-bundle]');
      const products=JSON.parse(offer.querySelector('[data-gnd-products]').textContent);
      refreshProduct(products[radio?.dataset.bundleKey]).catch(()=>{});
    } catch(_) {}
  };
  const warmFromIntent = event => {
    const offer=event.target.closest?.('gnd-offer');
    if(offer)warm(offer,event.target.closest('label')?.querySelector('[data-bundle]'));
  };
  for(const name of ['pointerover','pointerdown','focusin','change'])document.addEventListener(name,warmFromIntent,{passive:true});
  const observeOffers = () => {
    if(!('IntersectionObserver' in window))return;
    const observer=new IntersectionObserver(entries=>entries.forEach(entry=>{
      if(!entry.isIntersecting)return;
      observer.unobserve(entry.target);
      const run=()=>warm(entry.target);
      if('requestIdleCallback' in window)window.requestIdleCallback(run,{timeout:1500});else setTimeout(run,250);
    }),{rootMargin:'150px'});
    document.querySelectorAll('gnd-offer').forEach(offer=>observer.observe(offer));
  };
  if(document.readyState==='complete')observeOffers();else window.addEventListener('load',observeOffers,{once:true});
  document.addEventListener('visibilitychange',()=>{if(document.visibilityState==='hidden')productCache.clear();});
  window.addEventListener('pageshow',event=>{if(event.persisted)productCache.clear();});
})();
